import { AxeptioListener } from '@insify/axeptio-listener';
import { ProspectProvider } from '@insify/funnel-components';
import { ModalProvider } from '@insify/modal';
import {
  chainFilters,
  excludeDomains,
  excludeGTM,
  excludeIllegalInvocation,
  excludeUndefinedDataLayer,
} from '@insify/ui-utils';
import * as Sentry from '@sentry/react';
import { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import TagManager from 'react-gtm-module';
import { BrowserRouter } from 'react-router-dom';

import { Funnel } from './PropertyAndCasualty';
import { ABProvider } from './PropertyAndCasualty/components/ABProvider';
import { CoBrandingWrapper } from './PropertyAndCasualty/components/CoBrandingWrapper';
import { ExitPopup } from './PropertyAndCasualty/components/ExitPopup';
import { ObjectivTrackerProvider } from './PropertyAndCasualty/components/ObjectivTrackerProvider';
import { PncCommandsAndQueryProvider } from './PropertyAndCasualty/components/PncCommandsAndQueryProvider';
import { PromotionProvider } from './PropertyAndCasualty/hooks/usePromotion';
import { stepsContext } from './PropertyAndCasualty/hooks/useSteps';
import { storage } from './storage';
import { env, isProdOrStaging } from './util/env';

import type { TagManagerArgs } from 'react-gtm-module';

import '@insify/css/app.global.css';

import './translations';

if (isProdOrStaging) {
  const tagManagerArgs: TagManagerArgs = {
    gtmId: 'GTM-TPXPLKJ',
  };

  TagManager.initialize(tagManagerArgs);
}

if (isProdOrStaging) {
  Sentry.init({
    dsn: 'https://4d972724815a41ef968f2632c34140b0@o438756.ingest.sentry.io/4504088648810496',
    release: (import.meta.env.VITE_RELEASE_VERSION as string) || 'local',
    environment: env,
    beforeSend: chainFilters([
      excludeIllegalInvocation,
      excludeUndefinedDataLayer,
      excludeDomains(['doubleclick.net']),
      excludeGTM,
    ]),
    // https://docs.sentry.io/platforms/javascript/configuration/options/#normalize-depth
    normalizeDepth: 6,
    // https://docs.sentry.io/platforms/javascript/guides/gatsby/configuration/integrations/default/#dedupe
    integrations: (integrations) => {
      return integrations.filter((integration) => {
        return integration.name !== 'Dedupe';
      });
    },
  });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  // <React.StrictMode>
  <Sentry.ErrorBoundary fallback={<p>Something went wrong</p>}>
    <Suspense fallback={<></>}>
      <ProspectProvider storage={storage}>
        <ABProvider env={env}>
          <ModalProvider>
            <AxeptioListener>
              {(isConsentGiven) => (
                <ObjectivTrackerProvider isConsentGiven={isConsentGiven}>
                  <BrowserRouter>
                    <PncCommandsAndQueryProvider>
                      <PromotionProvider>
                        <stepsContext.Provider>
                          <CoBrandingWrapper>
                            <Funnel />
                            <ExitPopup />
                          </CoBrandingWrapper>
                        </stepsContext.Provider>
                      </PromotionProvider>
                    </PncCommandsAndQueryProvider>
                  </BrowserRouter>
                </ObjectivTrackerProvider>
              )}
            </AxeptioListener>
          </ModalProvider>
        </ABProvider>
      </ProspectProvider>
    </Suspense>
  </Sentry.ErrorBoundary>,
  // </React.StrictMode>,
);

// dummy comment
